import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { CancelWithConfirm } from '@/components/billing';
import {
  Subscription,
  cancelSubscription,
  extractBackendErrorMessage,
  postSubscription,
} from '@/services/hsm-backend';

export interface SubscriptionCardProps {
  subscription: Subscription;
  companyId: string;
  refetch: () => Promise<void>;
}

/**
 * "Suscripción" card with the renewal calendar (next renewal, last
 * renewal, default day-of-month) and an active/inactive status chip.
 *
 * @param subscription Subscription to render; required (caller hides the
 *                     whole card when the company has none).
 */
export const SubscriptionCard = ({
  subscription,
  companyId,
  refetch,
}: SubscriptionCardProps) => {
  const isActive = subscription.status === 'active';

  return (
    <Card>
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6">Suscripción</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              color={isActive ? 'success' : 'default'}
              label={isActive ? 'Activa' : 'Inactiva'}
            />
            {isActive ? (
              <CancelWithConfirm
                buttonLabel="Cancelar suscripción"
                title="Cancelar suscripción HSM"
                description="La suscripción quedará inactiva y se deshabilitarán las pestañas de Plan, Paquete y Crédito. El plan, los paquetes y la línea de crédito se conservan. Podés reactivarla más tarde."
                confirmLabel="Cancelar suscripción"
                successMessage="Suscripción cancelada"
                errorMessagePrefix="No se pudo cancelar la suscripción"
                onConfirm={() => cancelSubscription(companyId)}
                onConfirmed={refetch}
              />
            ) : (
              <ReactivateSubscriptionButton
                companyId={companyId}
                refetch={refetch}
              />
            )}
          </Stack>
        </Stack>
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          <Row
            label="Próxima renovación"
            value={formatLongDate(subscription.nextRenewalDate)}
          />
          <Row
            label="Última renovación"
            value={formatLongDate(subscription.lastRenewalDate)}
          />
          <Row
            label="Fecha por defecto"
            value={formatDayOfMonth(subscription.defaultRenewalDate)}
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

/**
 * Re-activates a cancelled (inactive) subscription. The backend revives the
 * existing row (resume semantics — no consumption reset), so this is a plain,
 * non-destructive button without a confirmation dialog. On success the shell
 * refetch flips the card back to active and re-enables the billing tabs.
 */
const ReactivateSubscriptionButton = ({
  companyId,
  refetch,
}: {
  companyId: string;
  refetch: () => Promise<void>;
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);

  const handleClick = async () => {
    setSubmitting(true);
    try {
      await postSubscription(companyId);
    } catch (error) {
      enqueueSnackbar(
        `No se pudo activar la suscripción: ${extractBackendErrorMessage(error)}`,
        { variant: 'error' }
      );
      setSubmitting(false);
      return;
    }
    // Success: notify, then refetch (which unmounts this button as the card
    // flips back to active). No state update after the refetch.
    enqueueSnackbar('Suscripción activada', { variant: 'success' });
    await refetch();
  };

  return (
    <Button
      size="small"
      variant="contained"
      disabled={submitting}
      onClick={() => void handleClick()}
    >
      Activar suscripción
    </Button>
  );
};

const Row = ({ label, value }: { label: string; value: string }) => (
  <Stack direction="row" spacing={3}>
    <Box sx={{ minWidth: 160 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
    <Typography variant="body2">{value}</Typography>
  </Stack>
);

function formatLongDate(value: string): string {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString('es-AR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatDayOfMonth(value: string): string {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return `${date.getDate()} de cada mes`;
}
